import { LineAuditEvent } from '../models/sales/line-audit-event';
import { fulfilmentRouteName } from './fulfilment-routes';

// The timeline of the sales order line audit (Sales Order What's Happened, requirement 4). The API
// sends CODES (D-9) and the words are here, in $localize. Two lists feed one timeline:
//  - LineAuditEvents mirrors TMSWeb.Common.Constants.LineAuditEvents - events read from the
//    existing tables (status items, stock transactions, picking lists, despatches);
//  - SalesOrderHistoryEventTypes mirrors TMSWeb.Common.Constants.SalesOrderHistoryEventTypes - the
//    new history, recorded from release (route, product, status, Allocation Complete, confirm, hold).
// Both must stay in step with the API.
export enum LineAuditEvents {
  lineStatus = 'LineStatus',
  purchaseOrderRaised = 'PurchaseOrderRaised',
  purchaseOrderReceived = 'PurchaseOrderReceived',
  manufacturingSheet = 'ManufacturingSheet',
  allocated = 'Allocated',
  deallocated = 'Deallocated',
  cut = 'Cut',
  moved = 'Moved',
  picked = 'Picked',
  unpicked = 'Unpicked',
  stockTransaction = 'StockTransaction',
  addedToPickingList = 'AddedToPickingList',
  loadedOnTransfer = 'LoadedOnTransfer',
  transferDeparted = 'TransferDeparted',
  receivedFromTransfer = 'ReceivedFromTransfer',
  despatchNote = 'DespatchNote',
  invoice = 'Invoice',
  creditNote = 'CreditNote',
}

export enum SalesOrderHistoryEventTypes {
  // Line events.
  demandStatus = 'DemandStatus',
  route = 'Route',
  product = 'Product',
  allocationComplete = 'AllocationComplete',
  unStick = 'UnStick',
  // Order events - shown on every line of the order.
  confirmed = 'Confirmed',
  unconfirmed = 'Unconfirmed',
  held = 'Held',
  takenOffHold = 'TakenOffHold',
}

// LineAuditDocument.kind. Mirrors TMSWeb.Common.Constants.LineAuditDocumentKinds.
export enum LineAuditDocumentKinds {
  pickList = 'PickList',
  transfer = 'Transfer',
  purchaseOrder = 'PurchaseOrder',
  manufacturingSheet = 'ManufacturingSheet',
  despatchNote = 'DespatchNote',
  invoice = 'Invoice',
  creditNote = 'CreditNote',
}

// A picking list's PickListType as stored (TMSWeb.Common.Constants.PickListTypes) - the audit's
// Documents grid and its "Added to picking list" events carry it.
export enum LineAuditPickListTypes {
  despatch = 'Despatch',
  transfer = 'Transfer',
  manufacturingMaterials = 'ManufacturingMaterials',
  sourcePick = 'SourcePick',
}

/** The event's name. */
export function lineAuditEventName(code: string | undefined | null): string {
  switch (code) {
    case LineAuditEvents.lineStatus:
      return $localize`Line status`;
    case LineAuditEvents.purchaseOrderRaised:
      return $localize`Purchase order raised`;
    case LineAuditEvents.purchaseOrderReceived:
      return $localize`Purchase order received`;
    case LineAuditEvents.manufacturingSheet:
      return $localize`Manufacturing sheet`;
    case LineAuditEvents.allocated:
      return $localize`Allocated`;
    case LineAuditEvents.deallocated:
      return $localize`Allocation removed`;
    case LineAuditEvents.cut:
      return $localize`Cut`;
    case LineAuditEvents.moved:
      return $localize`Moved`;
    case LineAuditEvents.picked:
      return $localize`Picked`;
    case LineAuditEvents.unpicked:
      return $localize`Un-picked`;
    case LineAuditEvents.stockTransaction:
      return $localize`Stock transaction`;
    case LineAuditEvents.addedToPickingList:
      return $localize`Added to picking list`;
    case LineAuditEvents.loadedOnTransfer:
      return $localize`Loaded on transfer`;
    case LineAuditEvents.transferDeparted:
      return $localize`Transfer departed`;
    case LineAuditEvents.receivedFromTransfer:
      return $localize`Received from transfer`;
    case LineAuditEvents.despatchNote:
      return $localize`Despatch note`;
    case LineAuditEvents.invoice:
      return $localize`Invoice`;
    case LineAuditEvents.creditNote:
      return $localize`Credit note`;
    case SalesOrderHistoryEventTypes.demandStatus:
      return $localize`Status changed`;
    case SalesOrderHistoryEventTypes.route:
      return $localize`Route changed`;
    case SalesOrderHistoryEventTypes.product:
      return $localize`Product changed`;
    case SalesOrderHistoryEventTypes.allocationComplete:
      return $localize`Allocation Complete changed`;
    case SalesOrderHistoryEventTypes.unStick:
      return $localize`Un-stuck`;
    case SalesOrderHistoryEventTypes.confirmed:
      return $localize`Order confirmed`;
    case SalesOrderHistoryEventTypes.unconfirmed:
      return $localize`Order unconfirmed`;
    case SalesOrderHistoryEventTypes.held:
      return $localize`Order put on hold`;
    case SalesOrderHistoryEventTypes.takenOffHold:
      return $localize`Order taken off hold`;
    default:
      // Anything unrecognised shows as sent rather than vanishing.
      return code ?? '';
  }
}

/**
 * The event as the timeline's Event cell reads it. A route change is a sentence, because the
 * history stores the route CODES ("ViaTransfer") and they are worded here: "Route changed from Via
 * Transfer to Local Stock" (AC-16). Everything else is the event's name; its old and new values go
 * in the Change cell (lineAuditEventChangeText).
 */
export function lineAuditEventText(event: LineAuditEvent | undefined | null): string {
  if (event?.code === SalesOrderHistoryEventTypes.route) {
    const from = fulfilmentRouteName(event.oldValue);
    const to = fulfilmentRouteName(event.newValue);

    if (from && to) {
      return $localize`Route changed from ${from} to ${to}`;
    }
    if (to) {
      return $localize`Route changed to ${to}`;
    }
  }

  return lineAuditEventName(event?.code);
}

/** "old → new", "→ new" or "old" - blank for a route change, which its Event cell already words. */
export function lineAuditEventChangeText(event: LineAuditEvent | undefined | null): string {
  if (!event || event.code === SalesOrderHistoryEventTypes.route) {
    return '';
  }

  const oldValue = event.oldValue ?? '';
  const newValue = event.newValue ?? '';

  if (oldValue && newValue) {
    return `${oldValue} → ${newValue}`;
  }

  return newValue ? `→ ${newValue}` : oldValue;
}

/**
 * The timeline's Details cell: what the event's parameters say, then a stock transaction's own
 * comment as stored (fixed English, deliberately - it is persisted data).
 */
export function lineAuditEventDetails(event: LineAuditEvent | undefined | null): string {
  if (!event) {
    return '';
  }

  const parameters = event.parameters ?? {};
  const details: string[] = [];

  switch (event.code) {
    case LineAuditEvents.allocated:
    case LineAuditEvents.deallocated:
      if (parameters['quantity']) {
        details.push($localize`Quantity ${parameters['quantity']}`);
      }
      break;
    case LineAuditEvents.purchaseOrderReceived:
      if (parameters['stock']) {
        details.push($localize`Stock ${parameters['stock']}`);
      }
      if (parameters['grn']) {
        details.push($localize`GRN ${parameters['grn']}`);
      }
      break;
    case LineAuditEvents.addedToPickingList:
      if (parameters['type']) {
        details.push(lineAuditPickListTypeName(parameters['type']));
      }
      break;
  }

  if (event.comments) {
    details.push(event.comments);
  }

  return details.join('; ');
}

/** A document's kind. */
export function lineAuditDocumentKindName(kind: string | undefined | null): string {
  switch (kind) {
    case LineAuditDocumentKinds.pickList:
      return $localize`Picking list`;
    case LineAuditDocumentKinds.transfer:
      return $localize`Transfer`;
    case LineAuditDocumentKinds.purchaseOrder:
      return $localize`Purchase order`;
    case LineAuditDocumentKinds.manufacturingSheet:
      return $localize`Manufacturing sheet`;
    case LineAuditDocumentKinds.despatchNote:
      return $localize`Despatch note`;
    case LineAuditDocumentKinds.invoice:
      return $localize`Invoice`;
    case LineAuditDocumentKinds.creditNote:
      return $localize`Credit note`;
    default:
      return kind ?? '';
  }
}

/** A picking list's type: "Source pick", "Despatch" (AC-8). */
export function lineAuditPickListTypeName(type: string | undefined | null): string {
  switch (type) {
    case LineAuditPickListTypes.despatch:
      return $localize`Despatch`;
    case LineAuditPickListTypes.transfer:
      return $localize`Transfer`;
    case LineAuditPickListTypes.manufacturingMaterials:
      return $localize`Manufacturing materials`;
    case LineAuditPickListTypes.sourcePick:
      return $localize`Source pick`;
    default:
      return type ?? '';
  }
}
